import { styled } from '@mui/material/styles';
import { FormattedMessage } from 'react-intl';
import Switch from '@/components/Switch/Switch';
import FileInput from '@/components/FileInput/FileInput';
import CloudUploadIcon from '@/assets/cloud-upload.svg?react';

interface UploadPhaseProps {
  file: File | null;
  onFileChange: (file: File | null) => void;
  viewComplianceDocs: boolean;
  onViewComplianceDocsChange: (checked: boolean) => void;
  editContacts: boolean;
  onEditContactsChange: (checked: boolean) => void;
  error?: string | null;
}

const UploadPhase = ({
  file,
  onFileChange,
  viewComplianceDocs,
  onViewComplianceDocsChange,
  editContacts,
  onEditContactsChange,
  error,
}: UploadPhaseProps) => {
  return (
    <Container>
      <Description>
        <FormattedMessage
          id="uploadContactsDescription1"
          defaultMessage="Create multi contacts in a bulk by uploading your contacts
        file."
        />
        <br />
        <b>
          <FormattedMessage
            id="uploadContactsDescription2"
            defaultMessage="Please make sure to use the"
          />{' '}
        </b>
        <a href="/assets/ContactsFileTemplate.xlsx" target="_blank">
          <FormattedMessage
            id="contactsFileTemplate"
            defaultMessage="Contacts File Template"
          />
        </a>
        <b>
          {' '}
          <FormattedMessage
            id="uploadContactsDescription3"
            defaultMessage="and save it as a CSV file."
          />
        </b>
      </Description>

      <Label>
        <FormattedMessage id="file" defaultMessage="File" />
        <Asterix>*</Asterix>
      </Label>
      <StyledFileInput
        value={file}
        onChange={onFileChange}
        renderContent={(fileName) =>
          fileName ? (
            <UploadContent>
              <CloudUploadIcon />
              <UploadText>{fileName}</UploadText>
            </UploadContent>
          ) : (
            <UploadContent>
              <CloudUploadIcon />
              <UploadText>
                <FormattedMessage
                  id="dragDropOrClickToUpload"
                  defaultMessage="Drag and drop your CSV file or click here to upload"
                />
              </UploadText>
            </UploadContent>
          )
        }
      />
      {error && <ErrorBox>{error}</ErrorBox>}
      <Label>
        <FormattedMessage
          id="permissions"
          defaultMessage="Permissions"
        />
      </Label>
      <SwitchContainer>
        <SwitchWrapper>
          <Switch
            checked={viewComplianceDocs}
            onChange={(e) =>
              onViewComplianceDocsChange(e.target.checked)
            }
            id="viewComplianceDocs"
            aria-label="View Compliance Documents"
          />
          <SwitchLabel htmlFor="viewComplianceDocs">
            <FormattedMessage
              id="viewComplianceDocuments"
              defaultMessage="View Compliance Documents"
            />
          </SwitchLabel>
        </SwitchWrapper>
        <SwitchWrapper>
          <Switch
            checked={editContacts}
            onChange={(e) => onEditContactsChange(e.target.checked)}
            id="editContacts"
            aria-label="Edit Contacts"
          />
          <SwitchLabel htmlFor="editContacts">
            <FormattedMessage
              id="editContacts"
              defaultMessage="Edit Contacts"
            />
          </SwitchLabel>
        </SwitchWrapper>
      </SwitchContainer>
    </Container>
  );
};

const Container = styled('div')(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(2),
}));

const Description = styled('p')(({ theme }) => ({
  fontSize: '16px',
  fontWeight: 400,
  lineHeight: '24px',
  margin: 0,
  marginBottom: theme.spacing(2),
  color: theme.palette.text.secondary,
}));

const Label = styled('div')(({ theme }) => ({
  fontSize: '16px',
  fontWeight: 500,
  lineHeight: '24px',
  marginBottom: theme.spacing(1),
  marginTop: theme.spacing(1),
}));

const Asterix = styled('span')(({ theme }) => ({
  color: theme.palette.red600,
  display: 'inline-block',
  marginLeft: theme.spacing(0.5),
}));

const SwitchContainer = styled('div')(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(2),
}));

const SwitchWrapper = styled('div')({
  display: 'flex',
  alignItems: 'center',
});

const SwitchLabel = styled('label')(({ theme }) => ({
  fontSize: '14px',
  fontWeight: 400,
  lineHeight: '20px',
  cursor: 'pointer',
  userSelect: 'none',
  color: theme.palette.text.primary,
  marginLeft: theme.spacing(1),
}));

const StyledFileInput = styled(FileInput)(({ theme }) => ({
  padding: theme.spacing(2),
  height: 'auto',
  cursor: 'pointer',
}));

const UploadContent = styled('div')(() => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: '12px',
}));

const UploadText = styled('div')(() => ({
  fontSize: '14px',
  lineHeight: '20px',
  textAlign: 'center',
}));

const ErrorBox = styled('div')(({ theme }) => ({
  padding: theme.spacing(2),
  backgroundColor: theme.palette.red50 || '#FEF2F2',
  border: `1px solid ${theme.palette.red500 || '#EF4444'}`,
  borderRadius: theme.radii?.xs || 4,
  color: theme.palette.red500 || '#EF4444',
  fontSize: '14px',
  lineHeight: '20px',
  marginTop: theme.spacing(1),
}));

export default UploadPhase;
